import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, Button, Card, Table, Badge, Select, Switch } from '@radix-ui/themes';
import { ChevronDownIcon, ChevronRightIcon, DownloadIcon, ReloadIcon } from '@radix-ui/react-icons';
import AdminLayout from './AdminLayout';
import {
  adminApi,
  EhrUploadIssueRow,
  EhrUploadIssuesResponse,
  EhrUploadIssueType,
  EhrUploadNotRetriedReason,
  EhrUploadPushHistoryItem,
} from '../../services/api';

/**
 * PHA-1223: per clinic, every patient whose care plan is not safely in the EHR.
 *
 * "ODA is counting on us that everything is working, so if something errored out, we need
 * to be the ones that are taking action." This is the list to act from: who failed, why,
 * whether Phanova is already retrying, and who to tell. Read-only — the fix for any one row
 * is on the patient's chart (or with the clinic / Cobalt), which a row click opens.
 */

const ISSUE_LABEL: Record<EhrUploadIssueType, string> = {
  failed: 'Failed',
  pending: 'No confirmation',
  not_uploaded: 'Never uploaded',
  resolved: 'Resolved',
};

const ISSUE_COLOR: Record<EhrUploadIssueType, 'red' | 'amber' | 'gray' | 'green'> = {
  failed: 'red',
  pending: 'amber',
  not_uploaded: 'gray',
  resolved: 'green',
};

const ISSUE_ORDER: EhrUploadIssueType[] = ['failed', 'pending', 'not_uploaded', 'resolved'];

const DAY_OPTIONS = [7, 14, 30, 90];

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

/** The "what now" column: who is acting on this row. */
export const nextStepFor = (row: EhrUploadIssueRow): string => {
  if (row.issue === 'resolved') return 'Filed on a later attempt — let the clinic know it is in the chart.';
  if (row.issue === 'not_uploaded') return 'Never sent — push it from the chart, or ask the clinic to.';
  if (row.issue === 'pending') return 'The EHR never confirmed it — check the chart in the EHR, then push again if missing.';
  const retry = row.auto_retry;
  if (retry?.scheduled) {
    return `Temporary — Phanova retries automatically (${retry.attempts} of ${retry.max_attempts} used${
      retry.next_at ? `, next after ${formatDateTime(retry.next_at)}` : ''
    }).`;
  }
  if (retry?.exhausted) return 'Temporary, but automatic retries ran out — push again; raise with Cobalt if it keeps failing.';
  if (retry?.transient) return 'Temporary — push it again from the chart.';
  const error = (row.error || '').toLowerCase();
  if (error.includes('patient not found')) return 'MRN not found in the EHR — fix the MRN with the clinic, then push again.';
  if (error.includes('folder not found')) return "Fix the clinic's EHR folder setting, then push again.";
  return 'Needs a person — read the error, fix the cause, push again.';
};

/**
 * Automatic retries used, separate from "Pushes" (how many times the plan was sent). A retry
 * reuses the push row, so it never shows up in the push count.
 */
export const autoRetryLabel = (row: EhrUploadIssueRow): string => {
  const retry = row.auto_retry;
  if (!retry) return '—';
  if (retry.attempts > 0 || (row.issue === 'failed' && retry.transient)) {
    return `${retry.attempts} of ${retry.max_attempts}`;
  }
  return row.issue === 'failed' ? 'Not retried' : '—';
};

const NOT_RETRIED_REASON: Record<EhrUploadNotRetriedReason, string> = {
  permanent: 'This error needs a person. Only temporary EHR errors are retried automatically.',
  disabled: 'Automatic retry is off in this environment.',
  exhausted: 'All automatic retries were used.',
  no_archive: 'No archived copy of the care plan to send again.',
  too_old: 'Sent more than 48 hours ago, too late for an automatic retry.',
  no_credential: 'The clinic has no EHR connection set up.',
  resent: 'An earlier copy of this plan is already in the EHR (or still being sent), so Phanova will not send a duplicate.',
  not_eligible: 'Not eligible for an automatic retry.',
};

/**
 * Why a failed row is not being retried. Zack, dev 2026-10-20: broke the folder on purpose and
 * waited for retries that never came, because "Folder not found" is not temporary.
 */
export const notRetriedReasonFor = (row: EhrUploadIssueRow): string | null => {
  if (row.issue !== 'failed') return null;
  const reason = row.auto_retry?.not_retried_reason;
  return reason ? NOT_RETRIED_REASON[reason] || NOT_RETRIED_REASON.not_eligible : null;
};

/** "Pushes" split by outcome, so "5" says which filed and which failed. */
export const pushBreakdown = (row: EhrUploadIssueRow): string => {
  const parts = [
    row.pushes_filed ? `${row.pushes_filed} filed` : null,
    row.pushes_failed ? `${row.pushes_failed} failed` : null,
  ].filter(Boolean);
  const other = row.attempts - (row.pushes_filed || 0) - (row.pushes_failed || 0);
  if (other > 0 && parts.length > 0) parts.push(`${other} waiting`);
  return parts.join(' · ');
};

const PUSH_STATUS_LABEL: Record<string, string> = {
  filed: 'Filed',
  superseded: 'Filed',
  accepted: 'Sent, EHR has not confirmed',
  pending: 'Sending',
  failed: 'Failed',
};

/** One line of the push history: who sent it, when, and what happened. */
export const describeHistoryItem = (item: EhrUploadPushHistoryItem): string => {
  const by = item.automatic ? 'Automatic upload' : item.pushed_by_name || 'Unknown';
  const status = PUSH_STATUS_LABEL[item.status || ''] || item.status || 'Unknown';
  const parts = [`${formatDateTime(item.requested_at)} · ${by} · ${status}`];
  if (item.folder_name) parts.push(`folder "${item.folder_name}"`);
  if (item.status === 'failed' && item.error) parts.push(item.error);
  if (item.auto_retries > 0) {
    parts.push(
      `automatic retries: ${item.auto_retries}${
        item.last_auto_retry_at ? `, last at ${formatDateTime(item.last_auto_retry_at)}` : ''
      }`,
    );
  }
  return parts.join(' — ');
};

/** A retry re-files the same push row, so its time lives in auto_retry, not requested_at. */
export const lastAttemptAt = (row: EhrUploadIssueRow): string | null => {
  const times = [row.requested_at, row.auto_retry?.last_attempt_at].filter(
    (t): t is string => Boolean(t),
  );
  if (times.length === 0) return row.approved_at;
  return times.reduce((a, b) => (new Date(b).getTime() > new Date(a).getTime() ? b : a));
};

const csvCell = (value: unknown): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const toCsv = (rows: EhrUploadIssueRow[]): string => {
  const header = [
    'Clinic', 'MRN', 'Issue', 'Error', 'Last attempt', 'Pushes', 'Pushes by outcome', 'Auto-retries',
    'Why not retried', 'Pushed by', 'Approved', 'Next step',
  ];
  const lines = rows.map((r) =>
    [
      r.clinic,
      r.mrn,
      ISSUE_LABEL[r.issue],
      r.error,
      lastAttemptAt(r),
      r.attempts,
      pushBreakdown(r),
      autoRetryLabel(r),
      notRetriedReasonFor(r),
      r.automatic ? 'Automatic upload' : r.pushed_by_name,
      r.approved_at,
      nextStepFor(r),
    ]
      .map(csvCell)
      .join(','),
  );
  return [header.join(','), ...lines].join('\n');
};

const AdminEhrUploadsPage: React.FC = () => {
  const navigate = useNavigate();

  const [data, setData] = useState<EhrUploadIssuesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [organizationId, setOrganizationId] = useState<string>('all');
  const [days, setDays] = useState<number>(30);
  const [includeResolved, setIncludeResolved] = useState(false);
  // Opt-in: a clinic that pushes by hand leaves many approved plans unsent on purpose.
  const [includeNotUploaded, setIncludeNotUploaded] = useState(false);
  const [issueFilter, setIssueFilter] = useState<string>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleHistory = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.listEhrUploadIssues({
        organization_id: organizationId === 'all' ? undefined : organizationId,
        days,
        include_resolved: includeResolved,
        include_not_uploaded: includeNotUploaded,
      });
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load EHR uploads');
    } finally {
      setLoading(false);
    }
  }, [organizationId, days, includeResolved, includeNotUploaded]);

  useEffect(() => { load(); }, [load]);

  const visibleIssues = ISSUE_ORDER.filter(
    (t) => (t !== 'resolved' || includeResolved) && (t !== 'not_uploaded' || includeNotUploaded),
  );

  const rows = useMemo(
    () => (data?.rows || []).filter((r) => issueFilter === 'all' || r.issue === issueFilter),
    [data, issueFilter],
  );

  const downloadCsv = () => {
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ehr-upload-issues-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout
      title="EHR Uploads"
      subtitle="Care plans that are not safely in the EHR, per clinic"
    >
      <Flex direction="column" gap="4">
        <Card>
          <Flex gap="5" wrap="wrap" align="center">
            {visibleIssues.map((t) => (
              <Box key={t}>
                <Text size="1" color="gray">{ISSUE_LABEL[t]}</Text>
                <Text as="div" size="6" weight="bold" data-testid={`total-${t}`}>
                  {data?.totals?.[t] ?? 0}
                </Text>
              </Box>
            ))}
            <Box>
              <Text size="1" color="gray">Automatic retry</Text>
              <Text as="div" size="3" data-testid="auto-retry-enabled">
                {data ? (data.auto_retry_enabled ? 'On' : 'Off in this environment') : '—'}
              </Text>
            </Box>
          </Flex>
        </Card>

        {data && data.by_clinic.length > 0 && (
          <Card>
            <Table.Root variant="surface" size="1">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Clinic</Table.ColumnHeaderCell>
                  {visibleIssues.map((t) => (
                    <Table.ColumnHeaderCell key={t}>{ISSUE_LABEL[t]}</Table.ColumnHeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data.by_clinic.map((c) => (
                  <Table.Row key={c.organization_id} data-testid={`clinic-summary-${c.organization_id}`}>
                    <Table.Cell>{c.clinic || c.organization_id}</Table.Cell>
                    {visibleIssues.map((t) => (
                      <Table.Cell key={t}>{c[t] || 0}</Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Card>
        )}

        <Card>
          <Flex gap="3" wrap="wrap" align="end">
            <Box>
              <Text size="1" color="gray" as="div" mb="1">Clinic</Text>
              {/* position="popper" — an item-aligned Radix Select renders empty here */}
              <Select.Root value={organizationId} onValueChange={setOrganizationId}>
                <Select.Trigger aria-label="Clinic" />
                <Select.Content position="popper">
                  <Select.Item value="all">All clinics with EHR upload</Select.Item>
                  {(data?.clinics || []).map((c) => (
                    <Select.Item key={c.id} value={c.id}>{c.name || c.id}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>
            <Box>
              <Text size="1" color="gray" as="div" mb="1">Issue</Text>
              <Select.Root value={issueFilter} onValueChange={setIssueFilter}>
                <Select.Trigger aria-label="Issue" />
                <Select.Content position="popper">
                  <Select.Item value="all">All issues</Select.Item>
                  {visibleIssues.map((t) => (
                    <Select.Item key={t} value={t}>{ISSUE_LABEL[t]}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>
            <Box>
              <Text size="1" color="gray" as="div" mb="1">Window</Text>
              <Select.Root value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <Select.Trigger aria-label="Window" />
                <Select.Content position="popper">
                  {DAY_OPTIONS.map((d) => (
                    <Select.Item key={d} value={String(d)}>Last {d} days</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>
            <Flex align="center" gap="2">
              <Switch checked={includeNotUploaded} onCheckedChange={(v) => setIncludeNotUploaded(!!v)} id="include-not-uploaded" />
              <Text size="2" as="label" htmlFor="include-not-uploaded">Show never uploaded</Text>
            </Flex>
            <Flex align="center" gap="2">
              <Switch checked={includeResolved} onCheckedChange={(v) => setIncludeResolved(!!v)} id="include-resolved" />
              <Text size="2" as="label" htmlFor="include-resolved">Show resolved</Text>
            </Flex>
            <Button variant="soft" onClick={() => load()} disabled={loading}>
              <ReloadIcon /> Refresh
            </Button>
            <Button variant="soft" onClick={downloadCsv} disabled={rows.length === 0}>
              <DownloadIcon /> CSV
            </Button>
          </Flex>
        </Card>

        {error && (
          <Card><Text color="red">{error}</Text></Card>
        )}

        <Card>
          <Box mb="3" data-testid="ehr-uploads-legend">
            <Text size="1" color="gray" as="div">
              <strong>Pushes</strong>: how many times this care plan was sent to the EHR, by a person or by
              automatic upload. Open one to see each push and what happened to it.
            </Text>
            <Text size="1" color="gray" as="div">
              <strong>Auto-retries</strong>: Phanova sends a push again by itself only when the EHR failed
              for a temporary reason (for example "failed after 4 attempts"): up to 3 times, about 30–45
              minutes apart, within 48 hours. Errors a person must fix, such as Folder not found or Patient
              not found, are never retried.
            </Text>
          </Box>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Clinic</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>MRN</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Issue</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Error</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Last attempt</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell title="Times this care plan was sent to the EHR">Pushes</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell title="Automatic retries Phanova has used for a temporary failure">
                  Auto-retries
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>By</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Next step</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.length === 0 && !loading && (
                <Table.Row>
                  <Table.Cell colSpan={9}>
                    <Text color="gray">Nothing to act on — no upload problems in this window.</Text>
                  </Table.Cell>
                </Table.Row>
              )}
              {rows.map((r) => {
                const key = `${r.organization_id}-${r.mrn}`;
                const history = r.history || [];
                const open = expanded.has(key);
                const breakdown = pushBreakdown(r);
                const whyNot = notRetriedReasonFor(r);
                return (
                <React.Fragment key={key}>
                <Table.Row
                  data-testid={`upload-row-${r.mrn}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/patients/${encodeURIComponent(r.mrn)}`)}
                >
                  <Table.Cell>{r.clinic || r.organization_id}</Table.Cell>
                  <Table.Cell>{r.mrn}</Table.Cell>
                  <Table.Cell>
                    <Flex gap="1" wrap="wrap">
                      <Badge color={ISSUE_COLOR[r.issue]}>{ISSUE_LABEL[r.issue]}</Badge>
                      {r.issue === 'failed' && r.auto_retry?.scheduled && (
                        <Badge color="blue" variant="soft">Retrying</Badge>
                      )}
                      {r.issue === 'failed' && r.in_ehr && (
                        <Badge color="green" variant="soft" title="An earlier copy of this plan is in the EHR">
                          Earlier copy filed
                        </Badge>
                      )}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell style={{ maxWidth: 280 }}>
                    <Text size="1">{r.error || '—'}</Text>
                  </Table.Cell>
                  <Table.Cell>{formatDateTime(lastAttemptAt(r))}</Table.Cell>
                  <Table.Cell data-testid={`pushes-${r.mrn}`}>
                    {history.length > 0 ? (
                      <Button
                        variant="ghost"
                        size="1"
                        aria-expanded={open}
                        aria-label={`Push history for MRN ${r.mrn}`}
                        onClick={(e) => {
                          // the row itself opens the chart
                          e.stopPropagation();
                          toggleHistory(key);
                        }}
                      >
                        {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
                        {r.attempts}
                      </Button>
                    ) : (
                      r.attempts
                    )}
                    {breakdown && (
                      <Text size="1" color="gray" as="div">{breakdown}</Text>
                    )}
                  </Table.Cell>
                  <Table.Cell data-testid={`auto-retries-${r.mrn}`} style={{ maxWidth: 220 }}>
                    {autoRetryLabel(r)}
                    {whyNot && (
                      <Text size="1" color="gray" as="div" data-testid={`not-retried-reason-${r.mrn}`}>
                        {whyNot}
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>{r.automatic ? 'Automatic upload' : r.pushed_by_name || '—'}</Table.Cell>
                  <Table.Cell style={{ maxWidth: 320 }}>
                    <Text size="1">{nextStepFor(r)}</Text>
                  </Table.Cell>
                </Table.Row>
                {open && (
                  <Table.Row data-testid={`push-history-${r.mrn}`}>
                    <Table.Cell colSpan={9} style={{ background: 'var(--gray-a2)' }}>
                      <Text size="1" weight="bold" as="div" mb="1">
                        Pushes of the current care plan, oldest first
                      </Text>
                      {history.map((item, i) => (
                        <Box key={item.push_id || i} mb="1">
                          <Text size="1" as="div">{i + 1}. {describeHistoryItem(item)}</Text>
                          {item.auto_retry_failures.map((f, j) => (
                            <Text key={j} size="1" color="gray" as="div" ml="4">
                              Failed before automatic retry {j + 1}
                              {f.failed_at ? ` (${formatDateTime(f.failed_at)})` : ''}: {f.error || 'no error text'}
                            </Text>
                          ))}
                        </Box>
                      ))}
                    </Table.Cell>
                  </Table.Row>
                )}
                </React.Fragment>
                );
              })}
            </Table.Body>
          </Table.Root>
          <Text size="1" color="gray" mt="2" as="div">
            {loading ? 'Loading…' : `${rows.length} patient${rows.length === 1 ? '' : 's'}`}
          </Text>
        </Card>
      </Flex>
    </AdminLayout>
  );
};

export default AdminEhrUploadsPage;
